/**
 * Activity Detail — view a single recorded outing with stats and charts.
 */

import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import '@material/web/progress/linear-progress.js'
import '@material/web/switch/switch.js'
import { db } from '../../database/schema.js'
import { featureStorage } from '../../storage/feature-storage.js'
import { activityStorage } from '../../storage/activity-storage.js'
import { syncChildrenOnNavigate, forcePushEntity } from '../../storage/sync-manager.js'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { router } from '../../utils/router.js'
import '../common/confirm-dialog.js'
import type { ConfirmDialog } from '../common/confirm-dialog.js'
import type { Activity } from '../../schemas/activity.js'
import type { Feature } from '../../schemas/feature.js'
import type { Plan } from '../../schemas/plan.js'
import type { Route } from '../../schemas/route.js'
import type { UnitSystem } from '../../utils/units.js'
import { convertDistance, convertElevation } from '../../utils/units.js'
import '../map/elevation-profile.js'
import '../map/distance-time-chart.js'
import './activity-track-map.js'
import type {
  ProfilePoint,
  NamedProfile,
  ElevationProfile,
  RestPeriod,
} from '../map/elevation-profile.js'
import type { DistanceTimePoint, DistanceTimeChart } from '../map/distance-time-chart.js'
import { processTrack } from '../../services/track/track-processing-service.js'
import { computeUncertaintyCorridor } from '../../services/track/uncertainty-corridor.js'
import {
  computeElevationStats,
  computeElevationProfile,
} from '../../services/track/elevation-metrics.js'
import type { ProcessedTrack } from '../../services/track/track-processing-service.js'
import { haversineDistance } from '../map/geo-utils.js'
import { reverseGeocodeService } from '../../services/geocode/reverse-geocode-service.js'

interface PaceSplit {
  /** Split index (0-based) */
  index: number
  /** Distance covered in this split, meters */
  distance: number
  /** Time spent in this split, seconds */
  duration: number
  /** Elevation gain in this split, meters */
  gain: number
}

interface SlopeBin {
  label: string
  /** Total distance traveled in this bin (meters) */
  distance: number
  /** Total time in this bin (seconds) */
  duration: number
}

@customElement('activity-detail')
export class ActivityDetail extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      background: var(--cairn-background-color, #f8fafc);
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .container {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
    }

    .content-scroll {
      flex: 1;
      overflow: auto;
      transition: transform 0.15s ease-out;
    }

    .header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex-shrink: 0;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
    }

    .back-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .back-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .sync-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      flex-shrink: 0;
    }

    .sync-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .sync-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .sync-button.pushing .material-icons {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .content {
      flex: 1;
      padding: var(--cairn-spacing-lg, 24px);
      max-width: 900px;
      width: 100%;
      box-sizing: border-box;
    }

    .meta-row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--cairn-spacing-md, 16px);
      align-items: center;
      margin-bottom: var(--cairn-spacing-md, 16px);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .meta-row .meta {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .meta-row .material-icons {
      font-size: 16px;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      display: inline-block;
    }

    .section {
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .section-title {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
      padding-bottom: var(--cairn-spacing-xs, 4px);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: var(--cairn-spacing-md, 16px);
    }

    .stat {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-md, 16px);
    }

    .stat-label {
      font-size: 12px;
      text-transform: uppercase;
      color: var(--cairn-text-secondary-color, #64748b);
      letter-spacing: 0.04em;
      margin-bottom: 4px;
    }

    .stat-value {
      font-size: 20px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
    }

    .chart {
      height: 160px;
    }

    .charts-stack > .chart + .chart {
      margin-top: 1px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--cairn-text-size-sm, 14px);
    }

    th,
    td {
      text-align: left;
      padding: 8px 12px;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    th {
      font-weight: 600;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    td.num {
      font-variant-numeric: tabular-nums;
    }

    .empty {
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
      padding: var(--cairn-spacing-sm, 8px) 0;
    }

    .related-row {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .related-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: var(--cairn-text-size-sm, 14px);
      padding: 4px 10px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-color, #1e293b);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
      font-family: inherit;
    }

    .related-chip:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .related-chip .material-icons {
      font-size: 14px;
    }

    .toggle-row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--cairn-spacing-md, 16px);
      margin-bottom: var(--cairn-spacing-md, 16px);
      align-items: flex-start;
    }

    .switch-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .switch-group label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      font-size: 13px;
      color: var(--cairn-text-color, #1e293b);
      min-width: 160px;
    }

    .toggle-group {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .toggle-group label {
      font-size: 12px;
      text-transform: uppercase;
      color: var(--cairn-text-secondary-color, #64748b);
      letter-spacing: 0.04em;
      margin-right: 4px;
    }

    .toggle-btn {
      font-size: 12px;
      font-family: inherit;
      padding: 4px 10px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      transition: all 0.15s;
    }

    .toggle-btn:first-of-type {
      border-radius: 6px 0 0 6px;
    }

    .toggle-btn:last-of-type {
      border-radius: 0 6px 6px 0;
    }

    .toggle-btn.active {
      background: var(--cairn-primary-color, #3b82f6);
      border-color: var(--cairn-primary-color, #3b82f6);
      color: #ffffff;
    }

    .toggle-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .legs-table td.leg-rest {
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
    }

    .legs-table td.leg-vehicle {
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
    }

    @media (min-width: 769px) {
      .header {
        display: none;
      }
    }
  `

  @property({ type: String }) activityId?: string
  @state() private activity: Activity | null = null
  @state() private syncing = false
  @state() private pushing = false
  @state() private trackFeature: Feature | null = null
  @state() private linkedPlan: Plan | null = null
  @state() private followedRoute: Route | null = null
  @state() private processedTrack: ProcessedTrack | null = null
  private cachedCorridor: GeoJSON.Position[] = []
  @state() private excludeDrives = true
  @state() private elevationSource: 'gps' | 'dem' = 'gps'
  @state() private demAvailable = false

  private settingsStorage = new SettingsStorage()

  private get units(): UnitSystem {
    return this.settingsStorage.getSettingsWithDefaults().unitSystem
  }

  override connectedCallback(): void {
    super.connectedCallback()
    if (this.activityId) {
      void this.loadActivity()
      this.syncing = true
      void syncChildrenOnNavigate('activity', this.activityId)
        .then(() => this.loadActivity())
        .finally(() => {
          this.syncing = false
        })
    }
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('activityId') && this.activityId) void this.loadActivity()
  }

  private async loadActivity(): Promise<void> {
    if (!this.activityId) return
    this.activity = (await db.activities.get(this.activityId)) ?? null
    if (this.activity) {
      const features = await featureStorage.getFeaturesForEntity('activity', this.activityId)
      this.trackFeature =
        features.find(f => f.type === 'track' && f.geometry.type === 'LineString') ?? null
      this.runTrackProcessing()
      await this.loadRelated()
      if (!this.activity.name) {
        void this.tryGeocodeActivityName()
      }
    } else {
      this.trackFeature = null
      this.linkedPlan = null
      this.followedRoute = null
      this.processedTrack = null
    }
  }

  private async tryGeocodeActivityName(): Promise<void> {
    if (!this.activity || !this.activityId) return
    const coords = this.trackCoords
    if (coords.length === 0) return

    const [lng, lat] = coords[0]
    try {
      const geo = await reverseGeocodeService.lookup(lat, lng)
      if (!geo?.displayName) return

      const distance = this.totalDistance
      const distStr = convertDistance(distance, this.units)
      const name = `${geo.displayName} (${distStr})`

      await activityStorage.updateActivity(this.activityId, { name })
      this.activity = { ...this.activity, name }
    } catch {
      // Still offline — will retry next time
    }
  }

  private formatDateTime(dateString?: string): string {
    if (!dateString) return 'Activity'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  private runTrackProcessing(): void {
    if (this.trackFeature?.geometry.type !== 'LineString') {
      this.processedTrack = null
      return
    }

    const props = (this.trackFeature.properties ?? {}) as Record<string, unknown>
    const coordAccuracies = props.coordAccuracies as (number | null)[] | undefined
    const coordActivities = props.coordActivities as (string | null)[] | undefined
    const coordDemElevations = props.coordDemElevations as (number | null)[] | undefined

    if (!coordAccuracies || !coordActivities) {
      this.processedTrack = null
      this.demAvailable = false
      return
    }

    const coords = (this.trackFeature.geometry as GeoJSON.LineString).coordinates
    const timestamps = (props.coordTimes as number[] | undefined) ?? []
    const coordSpeeds =
      (props.coordSpeeds as (number | null)[] | undefined) ??
      new Array<number | null>(coords.length).fill(null)
    const demElevations = coordDemElevations ?? new Array<number | null>(coords.length).fill(null)

    this.processedTrack = processTrack(
      coords,
      timestamps,
      coordAccuracies,
      coordActivities,
      coordSpeeds,
      demElevations
    )
    this.demAvailable = demElevations.some(e => e !== null)

    // Default toggles
    const hasVehicle = this.processedTrack.tags.includes('vehicle')
    this.excludeDrives = hasVehicle

    // Default elevation source based on availability
    this.elevationSource = this.demAvailable ? 'dem' : 'gps'

    this.computeCorridor()
  }

  private async loadRelated(): Promise<void> {
    if (!this.activityId) return
    const [planId, routeId] = await Promise.all([
      activityStorage.getLinkedPlanId(this.activityId),
      activityStorage.getFollowedRouteId(this.activityId),
    ])
    this.linkedPlan = planId ? ((await db.plans.get(planId)) ?? null) : null
    this.followedRoute = routeId ? ((await db.routes.get(routeId)) ?? null) : null
  }

  // --- Computed track data ---

  /** All coordinates from the track LineString — [lng, lat, ele?] */
  private get allCoords(): GeoJSON.Position[] {
    if (this.trackFeature?.geometry.type !== 'LineString') return []
    return (this.trackFeature.geometry as GeoJSON.LineString).coordinates
  }

  /** Coordinates for display and metrics — always uses smoothed track when available */
  private get trackCoords(): GeoJSON.Position[] {
    if (this.processedTrack) return this.processedTrack.cleanedCoords
    return this.allCoords
  }

  /** Rest segment midpoints for map display */
  private get restMidpoints(): [number, number][] {
    return this.processedTrack?.restSegments.map(r => r.midpoint) ?? []
  }

  private computeCorridor(): void {
    if (this.trackFeature?.geometry.type !== 'LineString') {
      this.cachedCorridor = []
      return
    }
    const coords = (this.trackFeature.geometry as GeoJSON.LineString).coordinates
    this.cachedCorridor = computeUncertaintyCorridor(coords, this.processedTrack?.tags)
  }

  /** All per-coordinate timestamps (ms epoch) */
  private get allCoordTimes(): number[] | null {
    const t = (this.trackFeature?.properties as { coordTimes?: unknown } | undefined)?.coordTimes
    return Array.isArray(t) && t.every(v => typeof v === 'number') ? t : null
  }

  /** Timestamps aligned with trackCoords */
  private get coordTimes(): number[] | null {
    if (this.processedTrack) return this.processedTrack.cleanedTimestamps
    return this.allCoordTimes
  }

  /** Cumulative distance at each coord index, in meters */
  private get cumDistances(): number[] {
    const coords = this.trackCoords
    const result: number[] = new Array(coords.length).fill(0) as number[]
    for (let i = 1; i < coords.length; i++) {
      result[i] = result[i - 1] + ActivityDetail.haversineM(coords[i - 1], coords[i])
    }
    return result
  }

  private get totalDistance(): number {
    const cd = this.cumDistances
    return cd.length > 0 ? cd[cd.length - 1] : 0
  }

  /** Elevations for the current elevationSource */
  private get currentElevations(): (number | null)[] {
    if (this.elevationSource === 'dem' && this.processedTrack) {
      return this.processedTrack.cleanedDemElevations
    }
    // GPS elevation from Z coordinate of smoothed track
    return this.trackCoords.map(c => (c.length >= 3 ? c[2] : null))
  }

  private get elevationStats(): { gain: number; loss: number; max: number; min: number } {
    return computeElevationStats(this.currentElevations, 3)
  }

  /** Total elapsed duration in seconds */
  private get totalDuration(): number {
    const ct = this.coordTimes
    if (ct && ct.length >= 2) return (ct[ct.length - 1] - ct[0]) / 1000
    if (this.activity?.startTime && this.activity.endTime) {
      return (
        (new Date(this.activity.endTime).getTime() - new Date(this.activity.startTime).getTime()) /
        1000
      )
    }
    return 0
  }

  /** Moving time (excludes rests) in seconds */
  private get movingDuration(): number | null {
    if (!this.processedTrack) return null
    return this.processedTrack.movingTimeMs / 1000
  }

  private get elevationProfile(): ProfilePoint[] {
    return computeElevationProfile(this.cumDistances, this.currentElevations)
  }

  /** Named profiles for overlay comparison */
  private get elevationProfiles(): NamedProfile[] {
    const coords = this.trackCoords
    const cd = this.cumDistances

    const gpsElevations = coords.map(c => (c.length >= 3 ? c[2] : null))
    const profiles: NamedProfile[] = [
      { name: 'GPS', points: computeElevationProfile(cd, gpsElevations), color: '#ff4444' },
    ]

    if (this.demAvailable && this.processedTrack) {
      profiles.push({
        name: 'Terrain',
        points: computeElevationProfile(cd, this.processedTrack.cleanedDemElevations),
        color: '#22c55e',
      })
    }

    return profiles
  }

  private get distanceTimeData(): DistanceTimePoint[] {
    const ct = this.coordTimes
    if (!ct || ct.length === 0) return []
    const cd = this.cumDistances
    const t0 = ct[0]
    return ct.map((t, i) => ({ elapsed: (t - t0) / 1000, distance: cd[i] }))
  }

  private get restPeriodsData(): RestPeriod[] {
    if (!this.processedTrack) return []
    const ct = this.coordTimes
    if (!ct || ct.length === 0) return []
    const t0 = ct[0]
    return this.processedTrack.restSegments.map(r => ({
      start: (r.startTime - t0) / 1000,
      end: (r.endTime - t0) / 1000,
    }))
  }

  /** Whether track analytics controls should be enabled */
  private get hasProcessingData(): boolean {
    return this.processedTrack !== null
  }

  /** Pace splits at each whole mile (or km in metric mode) */
  private get paceSplits(): PaceSplit[] {
    const ct = this.coordTimes
    if (!ct || ct.length < 2) return []
    const coords = this.trackCoords
    const cd = this.cumDistances
    const isMetric = this.units === 'metric'
    const splitMeters = isMetric ? 1000 : 1609.34

    const splits: PaceSplit[] = []
    let splitIdx = 0
    // Anchor marks the start of the current in-progress split
    let anchorDist = 0
    let anchorTime = ct[0]
    let splitGain = 0

    for (let i = 1; i < coords.length; i++) {
      const dStart = cd[i - 1]
      const dEnd = cd[i]
      const tStart = ct[i - 1]
      const tEnd = ct[i]
      const segDist = dEnd - dStart
      const segDt = tEnd - tStart
      const segGain =
        coords[i].length >= 3 && coords[i - 1].length >= 3
          ? Math.max(0, coords[i][2] - coords[i - 1][2])
          : 0

      // The portion of this segment that contributes to the in-progress split
      // grows as we walk past split boundaries within the segment.
      let segCursor = dStart // cumulative distance of the last boundary we crossed (or dStart)

      // Cross zero or more split boundaries within this segment
      for (;;) {
        const targetEnd = (splitIdx + 1) * splitMeters
        if (dEnd < targetEnd || segDist <= 0) break

        // Fraction of *the whole segment* at which the boundary lies
        const fracOfSeg = (targetEnd - dStart) / segDist
        const tBoundary = tStart + fracOfSeg * segDt
        // Gain for the slice of this segment from segCursor to targetEnd
        const sliceFrac = (targetEnd - segCursor) / segDist
        const sliceGain = segGain * sliceFrac

        splits.push({
          index: splitIdx,
          distance: targetEnd - anchorDist,
          duration: (tBoundary - anchorTime) / 1000,
          gain: splitGain + sliceGain,
        })

        anchorDist = targetEnd
        anchorTime = tBoundary
        splitGain = 0
        splitIdx++
        segCursor = targetEnd
      }

      // Residual of this segment after any boundaries belongs to the in-progress split
      if (segCursor < dEnd && segDist > 0) {
        const residualFrac = (dEnd - segCursor) / segDist
        splitGain += segGain * residualFrac
      }
    }

    // Final partial split
    const lastDist = cd[cd.length - 1]
    if (lastDist > anchorDist) {
      splits.push({
        index: splitIdx,
        distance: lastDist - anchorDist,
        duration: (ct[ct.length - 1] - anchorTime) / 1000,
        gain: splitGain,
      })
    }

    return splits
  }

  /** Distance + time bucketed by slope angle */
  private get slopeBins(): SlopeBin[] {
    const ct = this.coordTimes
    if (!ct || ct.length < 2) return []
    const coords = this.trackCoords
    const cd = this.cumDistances

    // Bins: descents and ascents at increasing severity
    const bins: { label: string; min: number; max: number }[] = [
      { label: 'Steep down', min: -Infinity, max: -15 },
      { label: 'Moderate down', min: -15, max: -5 },
      { label: 'Flat', min: -5, max: 5 },
      { label: 'Moderate up', min: 5, max: 15 },
      { label: 'Steep up', min: 15, max: Infinity },
    ]
    const out: SlopeBin[] = bins.map(b => ({ label: b.label, distance: 0, duration: 0 }))

    for (let i = 1; i < coords.length; i++) {
      const segDist = cd[i] - cd[i - 1]
      if (segDist === 0) continue
      if (coords[i].length < 3 || coords[i - 1].length < 3) continue
      const dz = coords[i][2] - coords[i - 1][2]
      const grade = (dz / segDist) * 100
      const segDuration = (ct[i] - ct[i - 1]) / 1000
      for (let bi = 0; bi < bins.length; bi++) {
        if (grade >= bins[bi].min && grade < bins[bi].max) {
          out[bi].distance += segDist
          out[bi].duration += segDuration
          break
        }
      }
    }

    return out.filter(b => b.distance > 0)
  }

  // --- Helpers ---

  private static haversineM(a: GeoJSON.Position, b: GeoJSON.Position): number {
    const toRad = (d: number): number => (d * Math.PI) / 180
    const R = 6371000
    const dLat = toRad(b[1] - a[1])
    const dLon = toRad(b[0] - a[0])
    const lat1 = toRad(a[1])
    const lat2 = toRad(b[1])
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
  }

  private formatDuration(seconds: number): string {
    if (!isFinite(seconds) || seconds <= 0) return '—'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    if (h > 0) return `${h}h ${m}m`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }

  /** Pace as time per unit distance (mile or km) */
  private formatPace(meters: number, seconds: number): string {
    if (meters <= 0 || seconds <= 0) return '—'
    const isMetric = this.units === 'metric'
    const unitMeters = isMetric ? 1000 : 1609.34
    const secPerUnit = (seconds / meters) * unitMeters
    const m = Math.floor(secPerUnit / 60)
    const s = Math.round(secPerUnit % 60)
    return `${m}:${String(s).padStart(2, '0')}/${isMetric ? 'km' : 'mi'}`
  }

  // --- Chart cursor sync ---

  private get elevTimeChart(): ElevationProfile | undefined {
    return this.shadowRoot?.querySelector('elevation-profile:not(.elev-dist)') as
      | ElevationProfile
      | undefined
  }

  private get elevDistChart(): ElevationProfile | undefined {
    return this.shadowRoot?.querySelector('elevation-profile.elev-dist') as
      | ElevationProfile
      | undefined
  }

  private get dtChart(): DistanceTimeChart | undefined {
    return this.shadowRoot?.querySelector('distance-time-chart') as DistanceTimeChart | undefined
  }

  private handleElevCursor(e: CustomEvent<{ elapsed: number | null }>): void {
    this.dtChart?.setCursorElapsed(e.detail.elapsed)
    this.elevDistChart?.setCursorElapsed(e.detail.elapsed)
  }

  private handleDtCursor(e: CustomEvent<{ elapsed: number | null }>): void {
    this.elevTimeChart?.setCursorElapsed(e.detail.elapsed)
    this.elevDistChart?.setCursorElapsed(e.detail.elapsed)
  }

  private handleElevDistCursor(e: CustomEvent<{ elapsed: number | null }>): void {
    this.elevTimeChart?.setCursorElapsed(e.detail.elapsed)
    this.dtChart?.setCursorElapsed(e.detail.elapsed)
  }

  private handleElevZoom(e: CustomEvent<{ min: number | null; max: number | null }>): void {
    this.elevDistChart?.setZoomRange(e.detail.min, e.detail.max)
    this.dtChart?.setZoomRange(e.detail.min, e.detail.max)
  }

  private handleDtZoom(e: CustomEvent<{ min: number | null; max: number | null }>): void {
    this.elevDistChart?.setZoomRange(e.detail.min, e.detail.max)
    this.elevTimeChart?.setZoomRange(e.detail.min, e.detail.max)
  }

  private handleElevDistZoom(e: CustomEvent<{ min: number | null; max: number | null }>): void {
    this.elevTimeChart?.setZoomRange(e.detail.min, e.detail.max)
    this.dtChart?.setZoomRange(e.detail.min, e.detail.max)
  }

  // --- Pull-to-refresh ---
  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async handlePushSync(): Promise<void> {
    if (!this.activityId) return
    this.pushing = true
    try {
      await forcePushEntity('activity', this.activityId)
    } finally {
      this.pushing = false
    }
  }

  private async handleDelete(): Promise<void> {
    if (!this.activity) return
    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Delete Activity'
      dialog.message = `Delete "${this.activity.name ?? 'this activity'}"? This will also delete associated track data and cannot be undone.`
      const confirmed = await dialog.show()
      if (!confirmed) return
    }
    await activityStorage.deleteActivity(this.activity.id)
    router.navigate('/activities')
  }

  private async refresh(): Promise<void> {
    if (!this.activityId) return
    this.syncing = true
    try {
      await syncChildrenOnNavigate('activity', this.activityId)
      await this.loadActivity()
    } finally {
      this.syncing = false
    }
  }

  private renderSegmentRows(): unknown {
    if (!this.processedTrack) return nothing
    const coords = this.processedTrack.smoothedCoords
    const allTimes = this.allCoordTimes
    let legNum = 0

    return this.processedTrack.segments.map(seg => {
      if (seg.type === 'vehicle') {
        return html`<tr>
          <td class="leg-vehicle">—</td>
          <td class="leg-vehicle" colspan="3">Drive</td>
        </tr>`
      }
      if (seg.type === 'rest') {
        const dur = allTimes ? (allTimes[seg.endIndex] - allTimes[seg.startIndex]) / 1000 : 0
        return html`<tr>
          <td class="leg-rest">—</td>
          <td class="leg-rest" colspan="3">Rested ${this.formatDuration(dur)}</td>
        </tr>`
      }

      legNum++
      let dist = 0
      for (let i = seg.startIndex; i <= seg.endIndex; i++) {
        if (i > seg.startIndex) {
          dist += haversineDistance(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0])
        }
      }
      const dur = allTimes ? (allTimes[seg.endIndex] - allTimes[seg.startIndex]) / 1000 : 0

      return html`<tr>
        <td class="num">${legNum}</td>
        <td class="num">${convertDistance(dist, this.units)}</td>
        <td class="num">${this.formatDuration(dur)}</td>
        <td class="num">${this.formatPace(dist, dur)}</td>
      </tr>`
    })
  }

  override render(): unknown {
    if (!this.activity) {
      return html`<div class="container">
        <div class="content"><p>Loading activity...</p></div>
      </div>`
    }

    const a = this.activity
    const hasTrack = this.trackCoords.length >= 2
    const elev = this.elevationStats
    const dist = this.totalDistance
    const dur = this.totalDuration
    const profile = this.elevationProfile
    const dtData = this.distanceTimeData
    const rests = this.restPeriodsData
    const splits = this.paceSplits
    const slopes = this.slopeBins
    const isMetric = this.units === 'metric'
    const splitUnit = isMetric ? 'km' : 'mi'

    return html`
      <div class="container">
        <div class="header">
          <button class="back-button" @click=${() => window.history.back()}>
            <span class="material-icons">arrow_back</span>
          </button>
          <h1 class="title">${a.name ?? this.formatDateTime(a.startTime)}</h1>
          <button
            class="sync-button ${this.pushing ? 'pushing' : ''}"
            @click=${this.handlePushSync}
            ?disabled=${this.pushing}
            title="Push to server"
          >
            <span class="material-icons">cloud_upload</span>
          </button>
          <button class="sync-button" @click=${this.handleDelete} title="Delete activity">
            <span class="material-icons">delete</span>
          </button>
        </div>
        ${this.syncing
          ? html`<md-linear-progress indeterminate class="sync-bar"></md-linear-progress>`
          : nothing}
        <div
          class="content-scroll"
          @touchstart=${this.handleTouchStart}
          @touchmove=${this.handleTouchMove}
          @touchend=${this.handleTouchEnd}
          @map-expand=${() => {
            router.navigate(`/activities/${this.activityId}/map`)
          }}
        >
          <div class="content">
            <div class="meta-row">
              <span class="meta"
                ><span class="material-icons">directions_walk</span>${a.activityType}</span
              >
              ${a.startTime
                ? html`<span class="meta"
                    ><span class="material-icons">schedule</span>${new Date(
                      a.startTime
                    ).toLocaleString()}</span
                  >`
                : nothing}
              ${a.conditions
                ? html`<span class="meta"
                    ><span class="material-icons">cloud</span>${a.conditions}</span
                  >`
                : nothing}
            </div>

            ${this.linkedPlan || this.followedRoute
              ? html`
                  <div class="related-row">
                    ${this.linkedPlan
                      ? html`
                          <button
                            class="related-chip"
                            @click=${() => router.navigate(`/plans/${this.linkedPlan!.id}`)}
                          >
                            <span class="material-icons">map</span>
                            Plan: ${this.linkedPlan.name}
                          </button>
                        `
                      : nothing}
                    ${this.followedRoute
                      ? html`
                          <button
                            class="related-chip"
                            @click=${() => router.navigate(`/routes/${this.followedRoute!.id}`)}
                          >
                            <span class="material-icons">alt_route</span>
                            Route: ${this.followedRoute.name}
                          </button>
                        `
                      : nothing}
                  </div>
                `
              : nothing}
            ${a.description ? html`<p>${a.description}</p>` : nothing}
            ${this.hasProcessingData
              ? html`
                  <div class="toggle-row">
                    <div class="switch-group">
                      <label>
                        <span>Exclude drives</span>
                        <md-switch
                          ?selected=${this.excludeDrives}
                          ?disabled=${!this.processedTrack?.tags.includes('vehicle')}
                          @change=${(e: Event) => {
                            this.excludeDrives = (
                              e.target as HTMLInputElement & { selected: boolean }
                            ).selected
                          }}
                        ></md-switch>
                      </label>
                    </div>
                    <div class="toggle-group">
                      <label>Elevation</label>
                      <button
                        class="toggle-btn ${this.elevationSource === 'gps' ? 'active' : ''}"
                        @click=${() => {
                          this.elevationSource = 'gps'
                        }}
                      >
                        GPS
                      </button>
                      <button
                        class="toggle-btn ${this.elevationSource === 'dem' ? 'active' : ''}"
                        ?disabled=${!this.demAvailable}
                        @click=${() => {
                          this.elevationSource = 'dem'
                        }}
                      >
                        Terrain
                      </button>
                    </div>
                  </div>
                `
              : nothing}
            ${hasTrack
              ? html`
                  <activity-track-map
                    .trackCoords=${this.trackCoords}
                    .rawTrackCoords=${this.allCoords}
                    .uncertaintyPolygon=${this.cachedCorridor}
                    .restPoints=${this.restMidpoints}
                  ></activity-track-map>

                  <div class="section">
                    <h2 class="section-title">Stats</h2>
                    <div class="stats-grid">
                      <div class="stat">
                        <div class="stat-label">Distance</div>
                        <div class="stat-value">${convertDistance(dist, this.units)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">
                          ${this.movingDuration !== null ? 'Elapsed' : 'Time'}
                        </div>
                        <div class="stat-value">${this.formatDuration(dur)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">Elevation Gain</div>
                        <div class="stat-value">${convertElevation(elev.gain, this.units)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">Elevation Loss</div>
                        <div class="stat-value">${convertElevation(elev.loss, this.units)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">Max Elevation</div>
                        <div class="stat-value">${convertElevation(elev.max, this.units)}</div>
                      </div>
                      <div class="stat">
                        <div class="stat-label">Avg Pace</div>
                        <div class="stat-value">${this.formatPace(dist, dur)}</div>
                      </div>
                      ${this.movingDuration !== null
                        ? html`
                            <div class="stat">
                              <div class="stat-label">Moving Time</div>
                              <div class="stat-value">
                                ${this.formatDuration(this.movingDuration)}
                              </div>
                            </div>
                            <div class="stat">
                              <div class="stat-label">Moving Pace</div>
                              <div class="stat-value">
                                ${this.formatPace(dist, this.movingDuration)}
                              </div>
                            </div>
                          `
                        : nothing}
                    </div>
                  </div>

                  <div class="section charts-stack">
                    <h2 class="section-title">Profile</h2>
                    <div class="chart">
                      <elevation-profile
                        class="elev-dist"
                        .trackProfile=${this.demAvailable ? [] : profile}
                        .profiles=${this.demAvailable ? this.elevationProfiles : []}
                        .distanceTime=${dtData}
                        .unitSystem=${this.units}
                        @chart-cursor=${this.handleElevDistCursor}
                        @chart-zoom=${this.handleElevDistZoom}
                      ></elevation-profile>
                    </div>
                    <div class="chart">
                      <elevation-profile
                        .trackProfile=${this.demAvailable ? [] : profile}
                        .profiles=${this.demAvailable ? this.elevationProfiles : []}
                        .distanceTime=${dtData}
                        .timeAxis=${true}
                        .showStats=${false}
                        .restPeriods=${rests}
                        .unitSystem=${this.units}
                        @chart-cursor=${this.handleElevCursor}
                        @chart-zoom=${this.handleElevZoom}
                      ></elevation-profile>
                    </div>
                    ${dtData.length >= 2
                      ? html`<div class="chart">
                          <distance-time-chart
                            .points=${dtData}
                            .restPeriods=${rests}
                            .unitSystem=${this.units}
                            @chart-cursor=${this.handleDtCursor}
                            @chart-zoom=${this.handleDtZoom}
                          ></distance-time-chart>
                        </div>`
                      : nothing}
                  </div>

                  <div class="section">
                    <h2 class="section-title">Pace by ${splitUnit}</h2>
                    ${splits.length > 0
                      ? html`<table>
                          <thead>
                            <tr>
                              <th>${splitUnit.toUpperCase()}</th>
                              <th>Time</th>
                              <th>Pace</th>
                              <th>Gain</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${splits.map(
                              s =>
                                html`<tr>
                                  <td class="num">${s.index + 1}</td>
                                  <td class="num">${this.formatDuration(s.duration)}</td>
                                  <td class="num">${this.formatPace(s.distance, s.duration)}</td>
                                  <td class="num">${convertElevation(s.gain, this.units)}</td>
                                </tr>`
                            )}
                          </tbody>
                        </table>`
                      : html`<div class="empty">No timing data available for splits.</div>`}
                  </div>

                  <div class="section">
                    <h2 class="section-title">Pace by Slope</h2>
                    ${slopes.length > 0
                      ? html`<table>
                          <thead>
                            <tr>
                              <th>Slope</th>
                              <th>Distance</th>
                              <th>Time</th>
                              <th>Pace</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${slopes.map(
                              b =>
                                html`<tr>
                                  <td>${b.label}</td>
                                  <td class="num">${convertDistance(b.distance, this.units)}</td>
                                  <td class="num">${this.formatDuration(b.duration)}</td>
                                  <td class="num">${this.formatPace(b.distance, b.duration)}</td>
                                </tr>`
                            )}
                          </tbody>
                        </table>`
                      : html`<div class="empty">No timing or elevation data for slope bins.</div>`}
                  </div>

                  ${this.processedTrack && this.processedTrack.segments.length > 1
                    ? html`
                        <div class="section">
                          <h2 class="section-title">Legs</h2>
                          <table class="legs-table">
                            <thead>
                              <tr>
                                <th>Leg</th>
                                <th>Distance</th>
                                <th>Duration</th>
                                <th>Pace</th>
                              </tr>
                            </thead>
                            <tbody>
                              ${this.renderSegmentRows()}
                            </tbody>
                          </table>
                        </div>
                      `
                    : nothing}
                `
              : html`<div class="section">
                  <div class="empty">
                    No moving segments detected in cleaned track. Switch to Full to see all data.
                  </div>
                </div>`}
          </div>
        </div>
      </div>
      <confirm-dialog></confirm-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'activity-detail': ActivityDetail
  }
}
